import { PlusSmIcon } from '@heroicons/react/outline';
import { initializeApp } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, signOut } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import React, { useRef, useState } from 'react';
import { animateButton, db, useOutsideClick } from '../../firebase';
// import axios from 'axios'; // Commented out - not needed when email sending is disabled
import AutomaticAdding from './AutomaticAdding';
import { ROLES } from '../../constants/roles';
import { toast } from 'react-toastify';
function AddUser({ selectedCompany }) {
  const [role, setRole] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userAdded, setUserAdded] = useState();
  const [mode, setMode] = useState('manual');
  const [progress, setProgress] = useState(0);
  const [autoUsers, setAutoUsers] = useState([]);
  const [rolesVisible, setRolesVisible] = useState(false);

  const rolesRef = useRef();
  useOutsideClick(rolesRef, () => setRolesVisible(false));

  async function addUserManual() {
    var config = {
      apiKey: process.env.REACT_APP_API_KEY,
      authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    };

    var secondaryApp = initializeApp(config, 'Secondary-Documentation-Tool');

    try {
      const newUserAuth = getAuth(secondaryApp);
      const userCredential = await createUserWithEmailAndPassword(newUserAuth, email, password);
      await signOut(newUserAuth);

      // Create user document with role directly in the users collection
      await setDoc(doc(db, 'user_data', selectedCompany.id, 'users', userCredential.user.uid), {
        firstName,
        lastName,
        email,
        role, // Store the role directly in the user document
      });

      // Send registration email - commented out for now
      // await axios.post('https://tax-mind-tools.de/registration-email', {
      //   users: [
      //     {
      //       firstName,
      //       lastName,
      //       email,
      //       password,
      //     },
      //   ],
      // });

      animateButton(setUserAdded, 2000);
      toast.success(`User ${email} created successfully`);

      // Reset form
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setRole('');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user: ' + error.message);
    }
  }

  async function addUsersAuto() {
    setProgress(0);

    var config = {
      apiKey: process.env.REACT_APP_API_KEY,
      authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    };

    var secondaryApp = initializeApp(config, 'Secondary-Documentation-Tool');
    const newAuth = getAuth(secondaryApp);

    var counter = 0;

    for (const user of autoUsers) {
      try {
        // Create user
        const userCredential = await createUserWithEmailAndPassword(
          newAuth,
          user['Email'],
          user['Passwort']
        );

        // Map old roles to new roles
        let newRole = 'editor'; // default role
        switch (user['Rolle']) {
          case 'Read-only':
            newRole = 'read-only';
            break;
          case 'Editor':
            newRole = 'editor';
            break;
          case 'Super-user':
            newRole = 'super-user';
            break;
          default:
            newRole = 'editor';
        }

        // Create user document with new role structure
        await setDoc(doc(db, 'user_data', selectedCompany.id, 'users', userCredential.user.uid), {
          firstName: user['Vorname'],
          lastName: user['Nachname'],
          email: user['Email'],
          role: newRole,
        });

        counter++;
        setProgress(Math.round((counter / autoUsers.length) * 100));
      } catch (error) {
        console.error('Error creating user:', error);
        continue;
      }
    }

    await signOut(newAuth);

    // Send registration emails - commented out for now
    // try {
    //   await axios.post('https://tax-mind-tools.de/registration-email', {
    //     users: autoUsers.map((user) => ({
    //       firstName: user['Vorname'],
    //       lastName: user['Nachname'],
    //       email: user['Email'],
    //       password: user['Passwort'],
    //     })),
    //   });
    //   toast.success(`Successfully created ${counter} users`);
    // } catch (error) {
    //   console.error('Error sending registration emails:', error);
    //   toast.warning(`Created ${counter} users but failed to send registration emails`);
    // }
    
    // Show success message without email sending
    toast.success(`Successfully created ${counter} users`);
  }

  return (
    <div className="component-container w-[100%] lg:w-[75%]">
      <div className="component-icon-container bg-indigo-400">
        <PlusSmIcon className="w-8 h-8" />
      </div>
      <p className="text-2xl p-1 m-1">Create User</p>
      <div className="mt-3">
        <div className="flex px-2 mb-1">
          <button
            onClick={() => setMode('manual')}
            className={
              'w-[50%] transition-all border border-indigo-400 rounded-lg p-1 mr-1 ' +
              (mode === 'manual' ? 'bg-indigo-400 text-white' : ' text-indigo-400')
            }
          >
            Manual
          </button>
          <button
            onClick={() => setMode('auto')}
            className={
              'w-[50%] transition-all border border-indigo-400 rounded-lg p-1 ' +
              (mode === 'auto' ? 'bg-indigo-400 text-white' : ' text-indigo-400')
            }
          >
            Automatic
          </button>
        </div>
        {mode === 'manual' && (
          <div className="mb-2 px-2">
            <div className="flex">
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="add-user-input w-[50%] mr-1"
                placeholder="First Name"
              />
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="add-user-input w-[50%]"
                placeholder="Last Name"
              />
            </div>
            <div ref={rolesRef} className="relative">
              <button
                className={
                  'border rounded-lg p-1 w-[100%] ' +
                  (role !== '' ? 'text-black' : 'text-neutral-400')
                }
                onClick={() => setRolesVisible((prev) => !prev)}
              >
                {role !== '' ? ROLES.find((r) => r.id === role)?.label : 'Select Role'}
              </button>
              <div
                className={
                  'absolute z-10 flex flex-col w-[100%] shadow-basic bg-white rounded-lg mt-1 p-1 pb-0 ' +
                  (rolesVisible ? '' : 'hidden')
                }
              >
                {ROLES.map((roleOption) => (
                  <button
                    key={roleOption.id}
                    onClick={() => {
                      setRole(roleOption.id);
                      setRolesVisible(false);
                    }}
                    className="p-1 mb-1 hover:bg-indigo-400 hover:text-white transition-all rounded-lg"
                  >
                    {roleOption.label}
                  </button>
                ))}
              </div>
            </div>

            <input
              className="add-user-input"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              placeholder="Email"
            />
            <input
              className="add-user-input"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              type="password"
              placeholder="Password"
            />
          </div>
        )}
        {mode === 'auto' && <AutomaticAdding progress={progress} setAutoUsers={setAutoUsers} />}

        <div className="mt-4 mx-2 relative overflow-hidden">
          <button
            className="w-[100%] p-2 bg-indigo-400 text-white rounded-lg hover:shadow-md transition-all"
            onClick={mode === 'manual' ? addUserManual : addUsersAuto}
          >
            Create User
          </button>
          <div
            className={
              'w-[100%] top-0 p-2 absolute rounded-lg bg-green-400 text-white transition-all text-center hover:shadow-md ' +
              (userAdded ? ' left-0' : ' left-[100%]')
            }
          >
            + User added
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddUser;
